/**
 * 콘텐츠 상세.
 *
 * 목록에서 답할 수 없는 두 가지에 답한다: **실제로 어떻게 보이나**, 그리고
 * **지금 어디에 나가고 있나**. 두 번째가 없으면 보관·삭제를 겁내게 되고,
 * 결국 아무도 정리하지 않아 목록이 계속 불어난다.
 */
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, LayoutTemplate, Pencil } from 'lucide-react';
import type { Asset, Me, SignageOneClient } from '../api/client.js';
import {
  Badge,
  Empty,
  ErrorNote,
  formatBytes,
  Loading,
  Panel,
  RelTime,
} from '../components/ui.js';
import { TemplatePreview } from '../components/TemplatePreview.js';
import { WebThumb } from '../components/MediaThumbs.js';
import { HtmlScreenFit } from '../components/HtmlScreenFit.js';
import { HtmlExternalNote } from '../components/HtmlExternalNote.js';
import { TemplateEditor } from './TemplateEditor.js';
import { ScreenEditor } from '../htmlEditor/ScreenEditor.js';
import { TemplateEntryModal } from './TemplateEntryModal.js';
import { AssetEditModal } from './AssetEditor.js';
import { TagEditor } from '../components/TagEditor.js';
import { formatActor } from './Activities.js';
import { useAction, useAsync } from '../hooks.js';
import { can } from '../capabilities.js';
import { reachRouteLine, reachRouteTitle } from '../content-usage.js';
import { validityBadge } from '../validity.js';
import { assetStatusBadge, useTranscodePolling } from '../transcode.js';
import { useTitleDetail } from '../components/pageTitle.js';
import { confirmDialog } from '../components/ConfirmDialog.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { Disclosure } from '../components/Disclosure.js';

const RETRANSCODE_TITLE = '화면이 못 트는 영상(H.264 High·4K·HEVC 등)을 모든 화면이 푸는 모양으로 다시 만듭니다';

const TYPE_LABEL: Record<Asset['type'], string> = {
  image: '이미지',
  video: '영상',
  web: '웹',
  template: '템플릿',
  html: 'HTML',
};

const STATUS_LABEL: Record<Asset['status'], string> = {
  processing: '처리 중',
  ready: '사용 가능',
  failed: '처리 실패',
};

export function ContentDetailScreen({ client, me }: { client: SignageOneClient; me: Me }): ReactNode {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const asset = useAsync(() => client.getAsset(id), [client, id]);
  /* 탭 제목에 콘텐츠 제목을. */
  useTitleDetail(`/content/${id}`, asset.data?.title);
  /*
   * 어느 경로로 어느 화면에 나가는가 — **서버가 센다**(`?include=reach`).
   *
   * 예전에는 여기서 플레이리스트·화면 전부(최대 5,000대)·스케줄·그룹·캠페인 다섯 목록을 받아 훑었다.
   * 콘텐츠 하나를 보려고 고객사의 편성을 통째로 내려받은 셈이다. 위의 `asset` 과 따로 묻는 것은, 변환
   * 진행을 따라가는 다시 읽기(4초)가 화면 배정까지 다시 훑지 않게 하려는 것이다 — 배정은 열 때 한 번이면 된다.
   */
  const reach = useAsync(() => client.getAsset(id, { reach: true }).then((found) => found.reach ?? null), [client, id]);
  const canAudit = can(me, 'audit:read');
  /*
   * 고치고 보관하고 지우는 것은 콘텐츠 변경(`content:write`)이다. 조회 역할에게 단추를 내밀면
   * 눌러 보고 나서야 403 으로 자기 권한을 알게 된다 — 출하 검사 2.4 에서 불합격이었다.
   */
  const canWrite = can(me, 'content:write');
  // 변환 중이면 진행률이 움직이게 다시 묻는다 — 끝나거나, 진행이 멈추거나, 탭이 뒤로 가면 멈춘다.
  useTranscodePolling(asset.data ? [asset.data] : [], asset.reload, 4000);
  /* 태그 드롭다운이 고를 값 — 쓰이는 콘텐츠 태그(화면 상세의 태그와 같은 꼴). */
  const tagNames = useAsync(() => client.listAssetTags(), [client]);
  /* 권한이 없으면 묻지도 않는다 — 어차피 403 이고, 그릴 카드도 없다. */
  const audit = useAsync(
    () =>
      canAudit
        ? client.auditLogs({ limit: '10', resource_type: 'asset', resource_id: id })
        : Promise.resolve(null),
    [client, id, canAudit],
  );
  const action = useAction();
  const [editing, setEditing] = useState(false);
  const [editingInfo, setEditingInfo] = useState(false);
  const [editingScreen, setEditingScreen] = useState(false);
  /** 템플릿으로 등록 · 이름·설명 고치기(2026-10-30). */
  const [registering, setRegistering] = useState(false);

  /** 플레이리스트 직접 배정뿐 아니라 스케줄·그룹·캠페인까지 — 규칙은 서버(`services/content-reach.ts`)에 있다. */
  const usedBy = reach.data?.routes ?? [];
  const reachedDisplays = reach.data?.displays ?? 0;

  if (asset.error) {
    return (
      <div className="screen">
        <BackLink />
        <ErrorNote message={asset.error} />
      </div>
    );
  }
  if (!asset.data) {
    return (
      <div className="screen">
        <BackLink />
        <Loading />
      </div>
    );
  }

  const info = asset.data;
  const canRetranscode =
    info.type === 'video' && info.transcode?.state !== 'queued' && info.transcode?.state !== 'running';
  const retranscode = (): void => void action.run(() => client.transcodeAsset(id), asset.reload);
  const toggleArchive = (): void =>
    void action.run(() => client.updateAsset(id, { archived: !info.archived }), asset.reload);
  /** 템플릿으로 등록할 수 있는 것 — 사본을 만들 수 있는 올린 HTML·템플릿. */
  const templatable = info.type === 'html' || (info.type === 'template' && Boolean(info.template));
  const entry = info.template_entry ?? null;

  return (
    <div className="screen animate-fade-in">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <BackLink />
        {canWrite ? (
          <div className="flex flex-wrap gap-2">
            {info.type === 'template' && info.template ? (
              <button type="button" className="btn" onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
                내용 편집
              </button>
            ) : null}
            {/* 화면 편집 — 화면을 보며 요소를 옮기고 크기를 바꾼다. 올린 HTML 과 템플릿(HTML 사본으로)만. */}
            {(info.type === 'html' || (info.type === 'template' && info.template)) && !info.archived ? (
              <button type="button" className="btn" onClick={() => setEditingScreen(true)}>
                <LayoutTemplate className="h-3.5 w-3.5" strokeWidth={2} />
                화면 편집
              </button>
            ) : null}
            {/* 템플릿 편집기에는 태그 칸이 없다. 정보 수정은 모든 종류에 둔다. */}
            <button type="button" className="btn" onClick={() => setEditingInfo(true)}>
              {info.type === 'template' ? null : <Pencil className="h-3.5 w-3.5" strokeWidth={2} />}
              정보 수정
            </button>
            {/*
              편집 둘만 밖에 두고 나머지는 [⋯] 뒤로(점진적 공개). 다만 **지금 해야 할 일은 밖으로 꺼낸다** —
              변환이 실패한 영상의 [다시 변환], 보관된 콘텐츠의 [복원].

              다시 변환 — 변환이 생기기 전에 올린 영상이나 실패한 변환. 다 될 때까지 원본이 계속 나간다.
              진행 중이면 두지 않는다.
            */}
            {canRetranscode && info.transcode?.state === 'failed' ? (
              <button type="button" className="btn" disabled={action.pending} title={RETRANSCODE_TITLE} onClick={retranscode}>
                다시 변환
              </button>
            ) : null}
            {info.archived ? (
              <button type="button" className="btn" disabled={action.pending} onClick={toggleArchive}>
                복원
              </button>
            ) : null}
            <MoreMenu
              size="normal"
              label="콘텐츠 작업 더 보기"
              items={[
                {
                  label: '템플릿으로 등록',
                  shown: templatable && !entry,
                  title: '[콘텐츠 추가] ▸ [템플릿 만들기] 에서 이 화면의 사본으로 새 콘텐츠를 만들게 합니다',
                  onSelect: () => setRegistering(true),
                },
                { label: '템플릿 이름·설명 고치기', shown: Boolean(entry), onSelect: () => setRegistering(true) },
                {
                  label: '템플릿 등록 해제',
                  shown: Boolean(entry),
                  disabled: action.pending,
                  title: '[템플릿 만들기] 에서만 빠집니다 — 이 콘텐츠는 그대로입니다',
                  onSelect: () =>
                    void action.run(
                      () => client.unregisterTemplate(id),
                      () => {
                        asset.reload();
                        audit.reload();
                      },
                      `「${entry?.name ?? info.title}」 을(를) 템플릿에서 뺐습니다`,
                    ),
                },
                {
                  label: '다시 변환',
                  shown: canRetranscode && info.transcode?.state !== 'failed',
                  disabled: action.pending,
                  title: RETRANSCODE_TITLE,
                  onSelect: retranscode,
                },
                { label: '보관', shown: !info.archived, disabled: action.pending, onSelect: toggleArchive },
                {
                  label: '삭제',
                  danger: true,
                  disabled: action.pending,
                  onSelect: async () => {
                    // 되돌릴 수 없는 동작이라 한 번 묻는다. 사용 중이면 서버가 막고,
                    // 그 이유(어느 플레이리스트인지)는 오류 메시지에 담겨 온다.
                    const ok = await confirmDialog({
                      title: '콘텐츠를 삭제할까요?',
                      body: (
                        <>
                          <strong>{info.title}</strong> — 되돌릴 수 없습니다.
                          {entry ? <> 등록한 템플릿 「{entry.name}」 에서도 빠집니다.</> : null}
                        </>
                      ),
                      confirmLabel: '삭제',
                      danger: true,
                    });
                    if (!ok) return;
                    void action.run(() => client.deleteAsset(id), () => navigate('/content'), `"${info.title}" 을(를) 삭제했습니다`);
                  },
                },
              ]}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">{info.title}</h1>
        <Badge tone="info">{TYPE_LABEL[info.type]}</Badge>
        {info.archived ? <Badge tone="neutral">보관됨</Badge> : null}
        {entry ? (
          <Badge tone="brand" title={`템플릿 「${entry.name}」 — [콘텐츠 추가] ▸ [템플릿 만들기] 에 나옵니다`}>
            템플릿으로 등록됨
          </Badge>
        ) : null}
        {(() => {
          const badge = assetStatusBadge(info);
          return badge ? (
            <span title={badge.title}>
              <Badge tone={badge.tone}>{badge.label}</Badge>
            </span>
          ) : null;
        })()}
      </div>

      {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <Panel
            title="미리보기"
            help={
              info.type === 'web'
                ? '스크립트를 실행하지 않고 그린 모습이라 실제 화면과 다를 수 있습니다. 화면에서 직접 확인하세요.'
                : undefined
            }
          >
            <div className="p-4">
              <AssetPreview client={client} asset={info} canWrite={canWrite} onChanged={asset.reload} />
            </div>
          </Panel>
        </div>

        <div className="flex flex-col gap-5">
          <Panel title="기본 정보">
            <div className="p-4">
              {/*
                운영에 쓰는 값(상태·게시 기간·해상도·태그)을 먼저, 참고 값(ID·크기·등록·수정)은 [자세히] 뒤로(점진적 공개).
                유형은 제목 옆 배지에 이미 있다.
              */}
              <dl className="kv">
                <dt>상태</dt>
                <dd>
                  {assetStatusBadge(info)?.label ?? STATUS_LABEL[info.status]}
                  {info.status === 'failed' && info.status_reason ? (
                    <small className="hint block">{info.status_reason}</small>
                  ) : null}
                </dd>
                {info.type === 'video' && info.transcode ? (
                  <>
                    {/* 화면에 무엇이 나가는지 — 변환본이 있으면 원본 대신 그것이 나간다. */}
                    <dt>재생본</dt>
                    <dd>
                      {info.playback === 'proxy' ? '변환본(H.264 MP4)' : '원본'}
                      {info.transcode.reasons.length > 0 ? (
                        <small className="hint block">변환한 까닭: {info.transcode.reasons.join(', ')}</small>
                      ) : null}
                    </dd>
                  </>
                ) : null}
                {/* 게시 기간 — 끝이 정해진 소재만. 상태와 나란히 두어 "왜 안 나오나" 를 여기서 읽는다. */}
                <dt>게시 기간</dt>
                <dd>
                  {validityText(info)}{' '}
                  {(() => {
                    const badge = validityBadge(info);
                    return badge ? <span className={`badge badge-${badge.tone}`}>{badge.label}</span> : null;
                  })()}
                </dd>
                {info.width && info.height ? (
                  <>
                    <dt>해상도</dt>
                    <dd>
                      {info.width} × {info.height}
                    </dd>
                  </>
                ) : null}
                {info.duration_sec ? (
                  <>
                    <dt>재생 길이</dt>
                    <dd>{info.duration_sec}초</dd>
                  </>
                ) : null}
                {/*
                  **태그 줄은 비어 있어도 둔다.** 예전에는 태그가 있을 때만 이 줄이 나왔다 —
                  아직 아무 태그도 없는 콘텐츠에서는 "태그" 라는 말 자체가 보이지 않아,
                  붙일 수 있다는 것도 어디서 붙이는지도 알 수 없었다. 화면 상세와 같은 자리에서
                  같은 드롭다운으로 붙인다(여기서는 붙이거나 빼는 즉시 저장된다).
                */}
                <dt>태그</dt>
                <dd>
                  {!canWrite ? (
                    <span>{info.tags.length > 0 ? info.tags.join(' · ') : '—'}</span>
                  ) : (
                    <TagEditor
                      tags={info.tags}
                      known={(tagNames.data?.items ?? []).map((item) => item.name)}
                      disabled={action.pending}
                      onChange={(tags) =>
                        void action.run(() => client.updateAsset(id, { tags }), () => {
                          asset.reload();
                          audit.reload();
                          tagNames.reload();
                        })
                      }
                    />
                  )}
                </dd>
              </dl>
              <Disclosure className="mt-3" title="자세히" summary="ID · 크기 · 등록·수정 시각">
                <dl className="kv">
                  <dt>ID</dt>
                  <dd className="mono">{info.id}</dd>
                  <dt>유형</dt>
                  <dd>{TYPE_LABEL[info.type]}</dd>
                  <dt>크기</dt>
                  <dd>{info.type === 'web' || info.type === 'template' ? '—' : formatBytes(info.size)}</dd>
                  <dt>등록</dt>
                  <dd><RelTime iso={info.created_at} /></dd>
                  <dt>수정</dt>
                  <dd><RelTime iso={info.updated_at} /></dd>
                </dl>
              </Disclosure>
            </div>
          </Panel>

          <Panel
            title={`배포 현황 · 화면 ${reachedDisplays}대`}
            help="플레이리스트·스케줄·그룹·캠페인을 거쳐 이 콘텐츠를 재생하는 화면입니다."
          >
            {reach.loading && !reach.data ? (
              <Loading />
            ) : reach.error ? (
              <p className="hint p-4">배포 현황을 불러오지 못했습니다: {reach.error}</p>
            ) : usedBy.length === 0 ? (
              <p className="hint p-4">사용 중인 플레이리스트·스케줄이 없습니다.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {usedBy.map((row) => (
                  <li key={row.key} className="px-4 py-2.5">
                    <Link
                      to={row.kind === 'schedule' ? '/schedules' : '/playlists'}
                      className="text-[13.5px] font-medium text-slate-700 hover:underline"
                    >
                      {reachRouteTitle(row)}
                    </Link>
                    <p className="hint">{reachRouteLine(row)}</p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          {/*
            변경 이력은 **감사로그를 볼 수 있는 사람에게만** 그린다. 권한이 없으면 이 카드가
            할 수 있는 일이 하나도 없어, 예전에는 "권한이 없습니다" 한 줄만 담긴 빈 카드가
            화면에 남았다.
          */}
          {canAudit ? (
          /* 변경 기록 — 접을 수 있고, 접으면 머리에 건수를 남긴다. */
          <Panel
            title="최근 변경"
            subtitle={audit.data ? `${audit.data.items.length}건` : undefined}
            collapsible
          >
            {audit.error ? (
              <p className="hint p-4">불러오지 못했습니다: {audit.error}</p>
            ) : (audit.data?.items.length ?? 0) === 0 ? (
              <p className="hint p-4">기록된 변경이 없습니다.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {audit.data?.items.map((entry) => (
                  <li key={entry.id} className="flex items-baseline gap-2 px-4 py-2 text-[12.5px]">
                    <span className="mono text-slate-500">{entry.action}</span>
                    <span className="flex-1 truncate text-slate-400">{formatActor(entry.actor).name}</span>
                    <span className="shrink-0 text-slate-400"><RelTime iso={entry.created_at} /></span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          ) : null}
        </div>
      </div>

      {editingInfo ? (
        <AssetEditModal
          client={client}
          asset={info}
          onClose={() => setEditingInfo(false)}
          onSaved={() => {
            setEditingInfo(false);
            asset.reload();
            audit.reload();
          }}
        />
      ) : null}

      {editingScreen ? (
        <ScreenEditor
          client={client}
          asset={info}
          canReplace={can(me, 'schedule:write')}
          onClose={() => setEditingScreen(false)}
          onSaved={() => {
            setEditingScreen(false);
            asset.reload();
            audit.reload();
          }}
        />
      ) : null}

      {registering ? (
        <TemplateEntryModal
          client={client}
          asset={info}
          onClose={() => setRegistering(false)}
          onSaved={() => {
            setRegistering(false);
            asset.reload();
            audit.reload();
          }}
        />
      ) : null}

      {editing && info.template ? (
        <TemplateEditor
          client={client}
          asset={info}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            asset.reload();
            audit.reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** 유형마다 볼 수 있는 것이 다르다 — 없는 것을 있는 척하지 않는다. */
function AssetPreview({
  client,
  asset,
  canWrite,
  onChanged,
}: {
  client: SignageOneClient;
  asset: Asset;
  canWrite: boolean;
  onChanged: () => void;
}): ReactNode {
  if (asset.type === 'template' && asset.template) {
    return (
      <div className="tpl-preview-frame">
        <TemplatePreview template={asset.template} location="미리보기" live />
      </div>
    );
  }

  if (asset.type === 'image' && asset.preview_url) {
    return (
      <div className="flex items-center justify-center overflow-hidden rounded-lg bg-slate-900">
        <img src={asset.preview_url} alt={asset.title} className="max-h-[420px] w-auto" />
      </div>
    );
  }

  if (asset.type === 'video' && asset.preview_url) {
    return (
      <video
        src={asset.preview_url}
        controls
        muted
        className="max-h-[420px] w-full rounded-lg bg-slate-900"
      />
    );
  }

  if (asset.type === 'html') {
    return (
      <>
        {/* 바깥 주소에서 불러오는 부분(2026-11-03) — 인터넷이 있어야 나오고, 그 파일이 바뀌면 화면도 바뀐다. */}
        <HtmlExternalNote origins={asset.html_external} />
        <HtmlScreenFit client={client} asset={asset} canWrite={canWrite} onChanged={onChanged} />
      </>
    );
  }

  if (asset.type === 'web') {
    return (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        <WebThumb asset={asset} className="aspect-video w-full" live />
        <div className="p-4">
          <p className="mono break-all text-slate-500">{asset.source_url}</p>
        </div>
      </div>
    );
  }

  return <Empty title="미리보기 없음" hint="표시할 파일이 없습니다." />;

}

function BackLink(): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  // 목록에서 왔으면 진짜 뒤로 간다 — 그 목록의 필터(주소)와 보던 자리로 돌아간다.
  const from = typeof (location.state as { from?: unknown } | null)?.from === 'string';
  return (
    <button
      type="button"
      className="flex w-fit items-center gap-1.5 text-[13.5px] text-slate-500 hover:text-slate-800"
      onClick={() => (from ? navigate(-1) : navigate('/content'))}
    >
      <ArrowLeft className="h-4 w-4" strokeWidth={2} />
      목록으로
    </button>
  );
}

/** 게시 기간 한 줄 — 없으면 '제한 없음'. */
function validityText(asset: { valid_from?: string | null; valid_until?: string | null }): string {
  const fmt = (iso: string): string =>
    new Date(iso).toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
  if (!asset.valid_from && !asset.valid_until) return '제한 없음';
  return `${asset.valid_from ? fmt(asset.valid_from) : '처음부터'} ~ ${asset.valid_until ? fmt(asset.valid_until) : '계속'}`;
}
